import { JitsiMeeting } from "@jitsi/react-sdk";
import { IJitsiMeetExternalApi } from "@jitsi/react-sdk/lib/types";
import { useContext, useRef, useState } from "react";
import VideoCallToolbar from "../VideoToolbar/VideoCallToolbar";
import VideoCallContext from "../VideoCallContext";
import Webcam from "react-webcam";
import { HIBOU_URL, VIDEO_CALL_DOMAIN } from "constants/env";
import { useParams } from "react-router";
import { VideoCallParams } from "types";
import { useWebUserContext } from "contexts";

export type VideoCallProps = {
  className?: string;
  username: string;
  onHangup?: () => void;
  roomName: string;
}

export const VideoCall = ({ className, username, onHangup, roomName }: VideoCallProps): JSX.Element => {
  const videoCallContext = useContext(VideoCallContext);
  const { selectedChannel, participantEmail } = useParams<VideoCallParams>();
  const [isToolbarDisplayed, setToolbarDisplayed] = useState<boolean>(false);
  const apiRef = useRef<IJitsiMeetExternalApi>();  

  let webUserContext = undefined;
  if (!participantEmail) {
    webUserContext = useWebUserContext();
  }
  
  const onVisioJoined = () => {
    if (apiRef.current.getParticipantsInfo().length <= 1) {
      void fetch(`${HIBOU_URL}/visio/notification/${roomName}/${selectedChannel}/${webUserContext?.webUser?.email ?? participantEmail}`, {method: "PUT"});
    }
    setToolbarDisplayed(true);
  }

  const updateJitsiStatus = () => {
    videoCallContext.setParticipantsNumber(apiRef.current.getNumberOfParticipants());
  }

  const handleApiReady = (api: IJitsiMeetExternalApi): void => {
    apiRef.current = api;

    api.addListener('videoConferenceJoined', () => onVisioJoined());
    api.addListener('videoConferenceLeft', () => setToolbarDisplayed(false));
    api.addListener('participantJoined', () => updateJitsiStatus());
    api.addListener('participantLeft', () => updateJitsiStatus());
    api.addListener('audioMuteStatusChanged', (data) => videoCallContext.setMicrophoneMuted(data.muted));
    api.addListener('videoMuteStatusChanged', (data) => videoCallContext.setCameraDisabled(data.muted));
    api.addListener('readyToClose', () => onHangup());
    updateJitsiStatus();
  };

  const isJitsiVisible = (): boolean => {
    return apiRef.current?.getNumberOfParticipants() > 1 || !onHangup;
  }

  return (
    <div className={`relative ${className}`}>
      { isToolbarDisplayed &&
        <VideoCallToolbar 
          className="absolute top-[100%] left-[50%] transform translate-x-[-50%] translate-y-[-130%]"
          isHangupButtonDisplayed={!!onHangup}
          apiRef={apiRef} />}
        <div className={`${!isJitsiVisible() && 'hidden'} h-full w-full`}>
          <JitsiMeeting
            domain={VIDEO_CALL_DOMAIN}
            roomName={roomName}
            getIFrameRef = { (iframeRef) => { iframeRef.style.height = '100%'; iframeRef.style.width = '100%'; } }
            userInfo = {{
              displayName: username,
              email: ''
            }}
            configOverwrite={{
              startWithAudioMuted: videoCallContext.isMicrophoneMuted,
              startWithVideoMuted: videoCallContext.isCameraDisabled,
              toolbarButtons: [],
              participantMenuButtonsWithNotifyClick: []
            }}
            onApiReady={handleApiReady} />
        </div>
        { !isJitsiVisible() && 
          <div className="flex items-center justify-center w-full h-full bg-black">
            <span className="self-center text-header2 text-white">En attente des participants...</span>
            {!videoCallContext.isCameraDisabled && 
              <Webcam className="absolute top-0w left-[100%] transform translate-x-[-100%]"
                width={screen.width > 768 ? 300 : 100} height={screen.width > 768 ? 200 : 65} />}
          </div> }
    </div>
  );
}